import { customSeriesDefaultOptions } from 'lightweight-charts'

/**
 * BandFillSeries — Relleno suave entre dos líneas (bandas VWAP).
 *
 * Dibuja el área entre `upper` y `lower` con un degradado vertical que va
 * desde el color de la banda (parte superior) hasta casi transparente
 * (parte inferior). Usa el mismo patrón que CloudFillSeries de SuperTrend.
 */
class BandFillPaneRenderer {
  constructor() {
    this._data = null
    this._options = null
  }

  update(data, options) {
    this._data = data
    this._options = options
  }

  draw(target, priceConverter) {
    if (!this._data || !this._options) return
    const { bars, visibleRange } = this._data
    if (!visibleRange || bars.length < 2) return

    const from = Math.max(0, Math.floor(visibleRange.from))
    const to = Math.min(bars.length - 1, Math.ceil(visibleRange.to))
    if (from >= to) return

    const baseColor = this._options.bandColor || '#B08D57'
    const baseOpacity = this._options.bandFillOpacity ?? 0.10

    target.useMediaCoordinateSpace(scope => {
      const ctx = scope.context

      const points = []
      let minY = Infinity
      let maxY = -Infinity
      for (let i = from; i <= to; i++) {
        const bar = bars[i]
        if (!bar?.originalData) continue
        const upper = bar.originalData.upper
        const lower = bar.originalData.lower
        if (!Number.isFinite(upper) || !Number.isFinite(lower)) continue
        const yUpper = priceConverter(upper)
        const yLower = priceConverter(lower)
        if (yUpper == null || yLower == null) continue
        points.push({ x: bar.x, y: yUpper })
        minY = Math.min(minY, yUpper, yLower)
        maxY = Math.max(maxY, yUpper, yLower)
      }
      for (let i = to; i >= from; i--) {
        const bar = bars[i]
        if (!bar?.originalData) continue
        const lower = bar.originalData.lower
        if (!Number.isFinite(lower)) continue
        const yLower = priceConverter(lower)
        if (yLower == null) continue
        points.push({ x: bar.x, y: yLower })
      }

      if (points.length > 2 && minY !== Infinity && maxY !== Infinity) {
        ctx.beginPath()
        let hasMove = false
        for (const p of points) {
          if (!hasMove) {
            ctx.moveTo(p.x, p.y)
            hasMove = true
          } else {
            ctx.lineTo(p.x, p.y)
          }
        }
        if (hasMove) {
          ctx.closePath()
          const gradient = ctx.createLinearGradient(0, maxY, 0, minY)
          gradient.addColorStop(0, this._withAlpha(baseColor, baseOpacity))
          gradient.addColorStop(0.35, this._withAlpha(baseColor, baseOpacity * 0.44))
          gradient.addColorStop(1, this._withAlpha(baseColor, 0.0))
          ctx.fillStyle = gradient
          ctx.fill()
        }
      }
    })
  }

  _withAlpha(color, alpha) {
    if (color.startsWith('rgba')) {
      return color.replace(/rgba\((\d+),\s*(\d+),\s*(\d+),\s*([\d.]+)\)/, `rgba($1, $2, $3, ${alpha})`)
    }
    if (color.startsWith('rgb(')) {
      const rgb = color.match(/\d+/g)
      return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`
    }
    if (color.startsWith('#')) {
      const hex = color.replace('#', '')
      const bigint = parseInt(hex, 16)
      const r = (bigint >> 16) & 255
      const g = (bigint >> 8) & 255
      const b = bigint & 255
      return `rgba(${r}, ${g}, ${b}, ${alpha})`
    }
    return color
  }
}

export class BandFillSeries {
  constructor() {
    this._renderer = new BandFillPaneRenderer()
  }

  renderer() {
    return this._renderer
  }

  update(data, seriesOptions) {
    this._renderer.update(data, seriesOptions)
  }

  priceValueBuilder(plotRow) {
    return [plotRow.upper, plotRow.lower]
  }

  isWhitespace(data) {
    return data.upper == null || data.lower == null
  }

  defaultOptions() {
    return {
      ...customSeriesDefaultOptions,
      color: 'rgba(0,0,0,0)',
      fillColor: 'rgba(255,255,255,0.1)',
      bandColor: '#B08D57',
      bandFillOpacity: 0.10,
    }
  }
}
